// AuthContext for managing authentication state and redirecting users
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import JwtService from "./services/jwtService";
import { User } from "../types/user";

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<any>;
  logout: () => void;
  signInWithToken: () => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is already logged in on app start
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const accessToken = JwtService.getAccessToken();
        const refreshToken = JwtService.getRefreshToken();

        if (accessToken && refreshToken) {
          // Try to verify token with backend
          const userData = await JwtService.signInWithToken();
          setUser(userData as User);
          setIsAuthenticated(true);
        }
      } catch (error) {
        // Token is invalid, clear storage
        JwtService.logout();
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = await JwtService.signInWithApiInstance(email, password);
      setUser(response.data.userData);
      setIsAuthenticated(true);
      return response;
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    JwtService.logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  const signInWithToken = async () => {
    try {
      const userData = await JwtService.signInWithToken();
      setUser(userData as User);
      setIsAuthenticated(true);
      return userData;
    } catch (error) {
      logout();
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        login,
        logout,
        signInWithToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
